import type { SessionStatePayload } from "@ext/agent/components/types/chat";
import type { StateStorage } from "zustand/middleware";

const PERSISTED_SESSIONS_LIMIT = 20;

type PersistedValue = {
	state: { activeSessionId?: string | null; sessions?: SessionStatePayload[] };
	version?: number;
};

const isQuotaExceeded = (error: unknown) => error instanceof DOMException && error.name === "QuotaExceededError";

const dropOldestSession = (sessions: SessionStatePayload[], activeSessionId: string | null): SessionStatePayload[] => {
	const oldest = sessions.findIndex((session) => session.id !== activeSessionId);
	return oldest === -1 ? [] : sessions.filter((_, index) => index !== oldest);
};

const writeDroppingOldestSessions = (storage: Storage, name: string, value: string) => {
	const persisted = JSON.parse(value) as PersistedValue;
	const activeSessionId = persisted.state.activeSessionId ?? null;
	let sessions = persisted.state.sessions ?? [];
	while (sessions.length) {
		sessions = dropOldestSession(sessions, activeSessionId);
		try {
			storage.setItem(name, JSON.stringify({ ...persisted, state: { ...persisted.state, sessions } }));
			return;
		} catch (error) {
			if (!isQuotaExceeded(error)) throw error;
		}
	}
};

export const keepLatestSessions = (
	sessions: SessionStatePayload[],
	activeSessionId: string | null,
): SessionStatePayload[] => {
	const latest = sessions.slice(-PERSISTED_SESSIONS_LIMIT);
	const active = sessions.find((session) => session.id === activeSessionId);
	return !active || latest.includes(active) ? latest : [active, ...latest.slice(1)];
};

export const withSessionsQuotaEviction = (storage: Storage): StateStorage => ({
	getItem: (name) => storage.getItem(name),
	removeItem: (name) => storage.removeItem(name),
	setItem: (name, value) => {
		try {
			storage.setItem(name, value);
		} catch (error) {
			if (!isQuotaExceeded(error)) throw error;
			writeDroppingOldestSessions(storage, name, value);
		}
	},
});
